import { useCallback, useEffect, useState } from 'react';
import { apiClient } from '../../../utils/apiClient';
import { DossierResource } from '../types';

/**
 * Charge la ressource "dossier" depuis l'API et expose son état sous
 * forme d'union discriminée. C'est l'unique source de vérité pour la
 * question "que doit afficher la route /dossiers/:id ?".
 *
 * Les 5 états possibles couvrent l'ensemble des réponses backend :
 *
 *   - `loading`           : requête en vol
 *   - `unauthorized`      : pas authentifié, ou 401 backend (compte
 *                           supprimé, session expirée)
 *   - `invitation`        : le dossier est partagé via invitation que
 *                           le user doit accepter/refuser (ou bypass
 *                           pour un admin)
 *   - `access-required`   : le dossier est privé et le user doit
 *                           demander un accès
 *   - `ready`             : dossier chargé, utilisateur autorisé
 *
 * Le retour expose aussi `refetch()` pour permettre au caller de
 * recharger après une action (acceptation d'invitation, restore de
 * version, etc.).
 */

interface UseDossierResourceArgs {
  dossierId: string | undefined;
  isAuthenticated: boolean;
}

interface UseDossierResourceReturn {
  resource: DossierResource;
  refetch: () => Promise<void>;
}

export function useDossierResource({
  dossierId,
  isAuthenticated,
}: UseDossierResourceArgs): UseDossierResourceReturn {
  const [resource, setResource] = useState<DossierResource>({ status: 'loading' });

  const load = useCallback(async () => {
    if (!isAuthenticated || !dossierId) {
      setResource({ status: 'unauthorized' });
      return;
    }

    setResource({ status: 'loading' });

    let dossierResponse: Response;
    try {
      dossierResponse = await apiClient(`/dossiers/${dossierId}`);
    } catch (error) {
      console.error('useDossierResource: erreur réseau /dossiers/:id', error);
      setResource({ status: 'unauthorized' });
      return;
    }

    if (dossierResponse.status === 401 || dossierResponse.status === 403) {
      setResource({ status: 'unauthorized' });
      return;
    }

    if (!dossierResponse.ok) {
      console.error('useDossierResource: HTTP', dossierResponse.status);
      setResource({ status: 'unauthorized' });
      return;
    }

    const data = await dossierResponse.json();

    if (data.invitation) {
      setResource({
        status: 'invitation',
        dossierTitle: data.dossier?.title ?? '',
        invitation: data.invitation,
        adminBypass: Boolean(data.adminBypass),
      });
      return;
    }

    if (data.accessRequired) {
      setResource({ status: 'access-required', dossier: data.dossier });
      return;
    }

    // Cas nominal : on a un dossier prêt. Le user role est résolu via
    // une requête séparée — si elle échoue on conserve `null` plutôt
    // que de bloquer l'utilisateur sur l'écran de chargement.
    let userRole: string | null = null;
    try {
      const roleResponse = await apiClient(`/dossiers/${dossierId}/role`);
      if (roleResponse.ok) {
        const roleData = await roleResponse.json();
        userRole = roleData?.role ?? null;
      }
    } catch (error) {
      console.error('useDossierResource: erreur récupération rôle', error);
    }

    setResource({
      status: 'ready',
      dossier: data,
      userRole,
      lastSaved: data.updatedAt ? new Date(data.updatedAt) : new Date(),
    });
  }, [dossierId, isAuthenticated]);

  useEffect(() => {
    void load();
  }, [load]);

  return { resource, refetch: load };
}
